<template>
  <article id="pool-select" class="dark" v-on:scroll="handleScroll">
    <ModelSelectHeader />
    <div id="non-sticky" ref="nonSticky">
      <PoolSelectNav ref="nav" :sticky="showStickyHeader" />
      <!-- placeholder for nav when it becomes fixed, to fill same space in -->
      <div v-show="showStickyHeader" :style="{ height: navHeight + 'px' }"></div>
    </div>
    <div class="content">
      <ModelSelect />
    </div>
  </article>
</template>
<script>
import ModelSelect from './../components/poolSelect/ModelSelect';
import ModelSelectHeader from './../components/poolSelect/ModelSelectHeader';
import PoolSelectNav from './../components/poolSelect/PoolSelectNav';
import { getModelByHandle, categoryOfPool, seriesOfPool } from '../poolData';

export default {
  name: 'PoolSelect',
  data() {
    return {
      showStickyHeader: false,
      navHeight: 0,
      resizeObserver: null
    };
  },
  components: {
    ModelSelect,
    ModelSelectHeader,
    PoolSelectNav,
  },
  methods: {
    handleScroll() {
      if (this.shouldShowStickyHeader()) {
        this.showStickyHeader = true;
      } else {
        this.showStickyHeader = false;
      }
      this.categoryInView = this.getCategoryInView();
    },
    shouldShowStickyHeader() {
      try {
        return this.$refs.nonSticky.getBoundingClientRect().y < 0;
      } catch (e) {
        //
      }
    },
    getCategoryInView() {
      // default value as the first category so there's always one selected.
      let categInView = this.$store.state.pools[0].tag;
      for (let i = 0; i < this.$store.state.pools.length; i++) {
        const el = document.getElementById(
          this.$store.state.pools[i].tag + '-group'
        );
        if (!el) return;
        // the last group el that is above the middle of the screen is the one we want.
        if (el.getBoundingClientRect().y < window.innerHeight / 2) {
          categInView = this.$store.state.pools[i].tag;
        }
      }
      return categInView;
    },
  },
  mounted() {
    // Set initial height and observe changes
    this.navHeight = this.$refs.nav.$el.offsetHeight;
    this.resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        this.navHeight = entry.contentRect.height;
      }
    });
    this.resizeObserver.observe(this.$refs.nav.$el);

    let params = new URLSearchParams(new URL(window.location).search);
    if (params.has('model')) {
      /*
        check if user had previously selected a model.
        we use the url parameter for this, not this.currPool.
        that is because param shows if user has selected a pool previously, but
        this.currPool is set before the user selects a pool.
      */
      let modelParam = params.get('model');
      const modelFromParam = getModelByHandle(modelParam);
      if (modelFromParam) {
        const category = categoryOfPool(modelFromParam.tag);
        this.$store.commit('setCategory', category);
        const series = seriesOfPool(modelFromParam.tag);
        this.$store.commit('setSeries', series);
      }
    } else if (params.has('family')) {
      // "family" ~= "category"
      let fam = params.get('family').toLowerCase().replaceAll('+', ' ');
      let catId, seriesId;
      
      // family is what you make it. it can be a category or a series. 
      switch (fam) {
        case 'swimspas':
        case 'swimspa':
        case 'swim spa':
        case 'swim':
        case 'spa':
          catId = 'spa';
          break;
        case 'originalseries':
        case 'modular':
        case 'original':
          catId = 'custom';
          break;
        case 'ep-pro-series':
          catId = 'custom';
          seriesId = 'pro';
          break;
        case 'ep-series':
          catId = 'custom';
          seriesId = 'ep';
          break;
        case 'ep-plus-series':
          catId = 'custom';
          seriesId = 'plus';
          break;
        case 'sp-series':
          catId = 'custom';
          seriesId = 'sp';
          break;
        case 'plunge':
          catId = 'custom';
          seriesId = 'plunge';
          break;
      }

      // Find the category object from poolData by matching tag
      const categoryObject = this.$store.state.pools.find(cat => cat.tag === catId);
      if (categoryObject) {
        this.$store.commit('setCategory', categoryObject);
      }

      if (seriesId) {
        const seriesObj = categoryObject.series.find(series => series.tag === seriesId);
        if (seriesObj) {
          this.$store.commit('setSeries', seriesObj);
        }
      }
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
};
</script>
<style lang="scss" scoped>
article#pool-select {
  position: absolute;
  z-index: 20;
  top: 0;
  width: 100vw;
  height: 100%;
  overflow-y: scroll;
  animation: fade-in 1000ms;
  background-color: $navy;
  @media #{ $desktop } {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }
  .content {
    width: 100%;
  }
  #non-sticky,
  #sticky-header-wrapper {
    background-color: $deep-navy;
  }
  #sticky-header-wrapper {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    &.show {
      display: block;
    }
  }
  #non-sticky {
    display: block;
    width: 100%;
  }
}
@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
// Sliding animations
.slide-left-enter-active,
.slide-right-enter-active {
  transition-duration: 160ms;
  transition-delay: 120ms;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
  overflow: hidden;
  @media #{ $desktop } {
    transition-duration: 240ms;
    transition-delay: 200ms;
  }
}
.slide-left-leave-active,
.slide-right-leave-active {
  transition-duration: 160ms;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
  overflow: hidden;
  @media #{ $desktop } {
    transition-duration: 200ms;
    overflow: unset;
  }
}
.slide-left-enter,
.slide-right-leave-active {
  opacity: 0;
  transform: translate(30vw, 0);
  @media #{ $desktop } {
    transform: translate(20vw, 0);
  }
}
.slide-left-leave-active,
.slide-right-enter {
  opacity: 0;
  transform: translate(-30vw, 0);
  @media #{ $desktop } {
    transform: translate(-20vw, 0);
  }
}
</style>
